import {
  AbilityAction,
  AbilityInterface,
  AbilitySubjectType,
  RawPermission,
  RawPermissions,
  abilitySubjects,
} from '@kinet-open-network/shared/data-access/model';

/**
 * Whether the acting user (via their own ability) is allowed to grant the given permission.
 * Master users can grant anything; granting the "all" subject requires holding the action
 * on every individual subject, since that's what a wildcard grant expands to server-side.
 */
export function canGrantPermission(
  ability: AbilityInterface,
  isMaster: boolean,
  permission: Pick<RawPermission, 'action' | 'subject'>,
): boolean {
  if (isMaster) {
    return true;
  }

  const action = permission.action as AbilityAction;
  const subjects = Array.isArray(permission.subject)
    ? permission.subject
    : [permission.subject];

  return subjects.every((subject) =>
    subject === 'all'
      ? abilitySubjects.every((concreteSubject) =>
          hasGrantedRule(ability, action, concreteSubject),
        )
      : ability.can(action, subject),
  );
}

// A subject-specific deny (e.g. carved out of a wildcard grant) shouldn't hide the
// underlying grant when deciding whether the user holds the "all" wildcard itself.
function hasGrantedRule(
  ability: AbilityInterface,
  action: AbilityAction,
  subject: AbilitySubjectType,
): boolean {
  return ability.rulesFor(action, subject).some((rule) => !rule.inverted);
}

function permissionKey(
  permission: Pick<RawPermission, 'action' | 'subject' | 'inverted'>,
) {
  return `${permission.subject}::${
    permission.action
  }::${!!permission.inverted}`;
}

/**
 * Returns permissions that changed (added or removed) between `previous` and `next`
 * which the acting user isn't allowed to grant themselves. Diffing avoids re-validating
 * permissions that were already present and are simply being resubmitted unchanged.
 */
export function getUngrantablePermissionChanges(
  ability: AbilityInterface,
  isMaster: boolean,
  previous: RawPermissions,
  next: RawPermissions,
): RawPermissions {
  if (isMaster) {
    return [];
  }

  const previousKeys = new Set(previous.map(permissionKey));
  const nextKeys = new Set(next.map(permissionKey));

  const changed = [
    ...next.filter(
      (permission) => !previousKeys.has(permissionKey(permission)),
    ),
    ...previous.filter(
      (permission) => !nextKeys.has(permissionKey(permission)),
    ),
  ];

  return changed.filter(
    (permission) => !canGrantPermission(ability, isMaster, permission),
  );
}
